// @givmo/app-charity — profile-edit validation (Zod) (CPA-WEB6, re-cut by CPA-WEB74).
//
// Client-side shape validation for the editable profile fields (mission / website / contact email /
// contact phone). The backend (`CharityProfileUpdateRequest`, `extra='forbid'`) is authoritative and
// re-validates; this schema is purely to gate the Save button and give inline feedback.
//
// PATCH semantics (matching `CharityProfileUpdateRequest`): only CHANGED fields are sent; an emptied
// field is sent as `null` to CLEAR it (the absent-vs-null convention). See `buildProfilePatch`.
//
// ── ONE WEBSITE FIELD (D-CP-73 / CPA-BE39) ──────────────────────────────────────────────────────
// `website` is a FORM field, not a wire field. There is no `website` key on the profile contract in
// either direction any more: the charity's website IS `organization_domain` (the normalized eTLD+1),
// which is what both shipped mobile apps already render as `https://{domain}`.
//
//   * `profileToForm` SEEDS the Website input by deriving `https://{organization_domain}`.
//   * `buildProfilePatch` SENDS the typed URL as `organization_domain`; the server harvests its
//     eTLD+1. It never emits a `website` key — that would now be an unknown key and 422.
//
// So the same input that used to edit a display string now edits the verification trust anchor, and
// a save that lands on a different registrable domain re-points the link both apps show and notifies
// every admin. That is the ruling, not an accident — see <DomainGuard>'s copy.

import { z } from 'zod';
import type { CharityProfileOut, CharityProfileUpdateRequest } from '@givmo/api-client';

export const MISSION_MAX = 2000;
export const WEBSITE_MAX = 2048;
export const CONTACT_EMAIL_MAX = 320;
export const CONTACT_PHONE_MAX = 40;

export const WEBSITE_MESSAGE = 'Enter a valid website URL (e.g. https://example.org)';
export const EMAIL_MESSAGE = 'Enter a valid email address';
export const MISSION_MAX_MESSAGE = `Mission must be ${MISSION_MAX} characters or fewer`;
export const CONTACT_PHONE_MAX_MESSAGE = `Contact phone must be ${CONTACT_PHONE_MAX} characters or fewer`;

/**
 * A lenient http(s) URL check. We accept a bare host too (e.g. "example.org") and normalize on submit;
 * the backend is the real authority. Empty string is allowed (it CLEARS the field on submit).
 */
export function isWebsiteish(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  // A scheme-qualified URL must parse; a bare host is accepted (we prepend https:// on submit).
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }
  // Bare host / host+path: at least one dot, no spaces.
  return /^[^\s/]+\.[^\s]+$/.test(trimmed);
}

/** A relaxed email check (empty is allowed → clears the field). The backend re-validates. */
export function isEmailish(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * The editable profile form values. NOTE `website` is a FORM key only: it round-trips through
 * `organization_domain` on the wire (CPA-WEB74), which is why this type is no longer a mirror of
 * `CharityProfileUpdateRequest`'s field names.
 */
export const profileSchema = z.object({
  // The form's counters and inline errors count UTF-16 units (`value.length`), so the Save gate counts
  // the same way. `.max()` counts code points from Zod 4.5, and would let emoji-heavy text the counter
  // shows over the limit through (CIS-SEC45).
  mission: z
    .string()
    .refine((value) => value.length <= MISSION_MAX, { message: MISSION_MAX_MESSAGE }),
  website: z.string().max(WEBSITE_MAX).refine(isWebsiteish, { message: WEBSITE_MESSAGE }),
  contact_email: z.string().max(CONTACT_EMAIL_MAX).refine(isEmailish, { message: EMAIL_MESSAGE }),
  contact_phone: z
    .string()
    .refine((value) => value.length <= CONTACT_PHONE_MAX, { message: CONTACT_PHONE_MAX_MESSAGE }),
});
export type ProfileFormValues = z.infer<typeof profileSchema>;

/** The four editable FORM keys (drives dirty-diffing + the patch builder). */
export const EDITABLE_FIELDS = ['mission', 'website', 'contact_email', 'contact_phone'] as const;
export type EditableField = (typeof EDITABLE_FIELDS)[number];

/** The three form keys that ARE wire keys; `website` is the one that is not. */
type DirectPatchField = Exclude<EditableField, 'website'>;

/**
 * The fields the backend's `charity_field_source` ledger admits — its `ck_charity_field_source_field`
 * CHECK, verbatim (givmo-backend-python `app/infrastructure/postgres/orm/charities.py`; last widened
 * by migration `acdd89f1c036`). A field outside this set can never carry a provenance row, so a hint
 * keyed on one can never light.
 */
export const LEDGER_FIELDS = [
  'organization_domain',
  'mission',
  'logo',
  'primary_category',
  'cause_tag',
  'page_content',
] as const;
export type LedgerField = (typeof LEDGER_FIELDS)[number];

/**
 * The `charity_field_source` LEDGER field each form field writes. A form field ABSENT from this map
 * carries no provenance and gets no "Edited by your organization" hint.
 *
 * `website` writes `organization_domain` (CPA-BE39) — the one name that differs. `contact_email` and
 * `contact_phone` are metadata display fields the ledger has never admitted, so they are absent on
 * purpose (CPA-WEB75, D-CP-78: their hints were removed rather than the CHECK widened — nothing
 * reads that provenance). The value type is what keeps this honest: mapping a form field to a name
 * the CHECK does not admit fails to compile, where a plain string showed nothing and failed nothing —
 * which is how the Website hint, and then the two contact hints, stayed dead.
 */
export const PROVENANCE_FIELD: Readonly<Partial<Record<EditableField, LedgerField>>> = {
  mission: 'mission',
  website: 'organization_domain',
};

/** The ledger field to look a form field's provenance up under; `null` when it carries none. */
export function provenanceFieldFor(field: EditableField): LedgerField | null {
  return PROVENANCE_FIELD[field] ?? null;
}

/**
 * The charity's website, derived from its ONE stored field. The apps build exactly this URL from
 * `organization_domain`, so showing anything else in the input would show the Owner a value the
 * product does not have. A charity with no anchor yet gets an empty input, not `https://`.
 */
export function websiteFromDomain(domain: string | null | undefined): string {
  const trimmed = domain?.trim() ?? '';
  return trimmed.length === 0 ? '' : `https://${trimmed}`;
}

/**
 * Seed the form from the loaded profile — nulls become empty strings so the inputs are controlled.
 * `logo_url` is intentionally NOT part of the editable form. `website` is DERIVED from
 * `organization_domain` (CPA-WEB74): the profile read carries no website of its own.
 */
export function profileToForm(profile: CharityProfileOut): ProfileFormValues {
  return {
    mission: profile.mission ?? '',
    website: websiteFromDomain(profile.organization_domain),
    contact_email: profile.contact_email ?? '',
    contact_phone: profile.contact_phone ?? '',
  };
}

/** Normalize a website input to a canonical http(s) URL on submit (prepend https:// to a bare host). */
export function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Build a MINIMAL PATCH body: only fields that CHANGED from the loaded profile are included; a field
 * emptied by the user is sent as `null` (clear), a filled field is sent as its (normalized) value, and an
 * unchanged field is OMITTED entirely (the backend leaves it untouched). Returns `null` when nothing
 * changed, so the caller can no-op instead of firing an empty PATCH.
 *
 * **The Website input emits `organization_domain`, never `website`** (CPA-WEB74). Its baseline is the
 * DERIVED `https://{organization_domain}`, not a stored string, so:
 *
 *   * typing the bare host of the domain already stored is NOT a change — `normalizeWebsite` prepends
 *     `https://` and it matches the derived baseline, so nothing is sent (the round-trip rule this had
 *     before, re-derived against the new baseline);
 *   * a deeper URL on the SAME registrable domain IS sent, because the client cannot know the server's
 *     Public Suffix answer; the server harvests the same eTLD+1, so the anchor does not move and no
 *     admin is notified. Sending it is the honest choice — guessing the harvest here would be the
 *     client deciding a verification question;
 *   * a URL on a DIFFERENT registrable domain is sent and re-points the anchor, which is the ruling.
 *
 * A CLEARED Website sends `organization_domain: null`, keeping this builder's one clear convention. The
 * anchor cannot be emptied, so the server refuses it (400, "Please enter your organization's website
 * address…") and the form's toast shows that sentence. Deliberately not silently dropped: a Save that
 * appears to do nothing is worse than a Save that says why it did nothing.
 */
export function buildProfilePatch(
  original: CharityProfileOut,
  values: ProfileFormValues,
): CharityProfileUpdateRequest | null {
  const patch: CharityProfileUpdateRequest = {};
  let changed = false;

  const put = (key: DirectPatchField, next: string) => {
    const before = (original[key] ?? '') as string;
    const nextNorm = next.trim();
    if (nextNorm === before.trim()) return;
    changed = true;
    patch[key] = nextNorm.length === 0 ? null : nextNorm;
  };

  // The website: compared on the normalized form against the DERIVED baseline, emitted as the anchor.
  const nextWebsite = normalizeWebsite(values.website);
  if (nextWebsite !== websiteFromDomain(original.organization_domain)) {
    changed = true;
    patch.organization_domain = nextWebsite.length === 0 ? null : nextWebsite;
  }

  put('mission', values.mission);
  put('contact_email', values.contact_email);
  put('contact_phone', values.contact_phone);

  return changed ? patch : null;
}
